<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router';
import { computed, onErrorCaptured, onMounted, ref } from 'vue';
import MobileNav from '@/components/layout/MobileNav.vue';
import ToastHost from '@/components/ui/ToastHost.vue';
import RouteProgress from '@/components/layout/RouteProgress.vue';
import Customer360 from '@/components/dashboard/Customer360.vue';
import CommandPalette from '@/components/layout/CommandPalette.vue';
import HelpModal from '@/components/layout/HelpModal.vue';
import { useTheme } from '@/composables/useTheme';
import { useCustomer360 } from '@/composables/useCustomer360';
useTheme();

const customer = useCustomer360();

const route = useRoute();
const isAuthLayout = computed(() => route.meta.layout === 'auth');

// Global error boundary — przy crashu komponentu pokazujemy fallback zamiast czarnej strony
const renderError = ref<Error | null>(null);

onErrorCaptured((err) => {
  console.error('App render error:', err);
  const msg = (err as Error)?.message ?? '';
  // Chunk errors — niech lazy-route + SW odswieza, my nie pokazujemy crashu
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  ) {
    return false;
  }
  renderError.value = err instanceof Error ? err : new Error(String(err));
  return false;
});

function reloadApp() {
  renderError.value = null;
  if (typeof window !== 'undefined') window.location.reload();
}

// PWA: gdy nowy SW przejmie kontrole, automatyczny soft reload przy nastepnej nawigacji
onMounted(() => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    // Krotki delay zeby unikac petli reload
    window.setTimeout(() => window.location.reload(), 100);
  });
});
</script>

<template>
  <div class="min-h-screen" :class="{ 'pb-20 md:pb-0': !isAuthLayout }">
    <RouteProgress />

    <!-- Error fallback (zapobiega czarnej stronie przy runtime crashach) -->
    <div v-if="renderError" class="min-h-screen flex items-center justify-center px-4">
      <div class="card card-featured p-8 max-w-md text-center">
        <h1 class="text-xl font-bold mb-2">Cos poszlo nie tak</h1>
        <p class="text-sm mb-4" :style="{ color: 'var(--surface-fg-2)' }">
          {{ renderError.message || 'Nieznany blad' }}
        </p>
        <button class="btn btn-accent" @click="reloadApp">Odswiez aplikacje</button>
      </div>
    </div>

    <RouterView v-else v-slot="{ Component }">
      <transition name="route">
        <component :is="Component" v-if="Component" :key="route.path" />
        <!-- Fallback gdy chunk sie ladowuje — szkielet zamiast czarnej strony -->
        <div v-else class="min-h-screen flex items-center justify-center">
          <div class="flex flex-col items-center gap-3 opacity-70">
            <div class="route-spinner" />
            <p class="text-caps" :style="{ color: 'var(--surface-fg-faint)' }">ladowanie...</p>
          </div>
        </div>
      </transition>
    </RouterView>

    <MobileNav v-if="!isAuthLayout && !renderError" />
    <ToastHost />
    <Customer360
      :open="customer.isOpen.value"
      :profile="customer.currentProfile.value"
      @update:open="(v) => !v && customer.close()"
    />
    <CommandPalette />
    <HelpModal />
  </div>
</template>

<style>
.route-enter-active {
  transition: opacity 0.25s var(--ease-smooth);
}
.route-enter-from {
  opacity: 0;
}

.route-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid var(--border-soft);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: route-spin 0.7s linear infinite;
}

@keyframes route-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
